/* eslint-disable no-nested-ternary */
import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import moment from 'moment-timezone';
import IcomoonReact from 'icomoon-react';
import { FixedSizeList as List } from 'react-window';
import { AutoSizer } from 'react-virtualized';

import generateUniqueId from '../../../../shared/utils/generateUniqueId';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { getExerciseOptions, searchExerciseOptions } from '../../utils/exercisePickerOptions';
import {
  setActiveModal,
  addPreSavedItemToCalendarItemsArray,
  addPreSavedItemToProgramItemsArray,
} from '../../ducks/calendarActions';

/**
 * Full-screen exercise picker for phones. Replaces the react-select dropdown
 * used on desktop (SelectDropdownExercisePicker), whose portaled popup covers
 * the search input / floats off-screen on small viewports. Rendered only on
 * phones by ExercisePicker; desktop is untouched.
 *
 * Self-contained on purpose: it reads the same Redux data and reproduces the
 * same add + create-exercise dispatches as the dropdown, so no shared
 * (desktop-affecting) code is refactored.
 */

const Overlay = styled('div')`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const SearchAndIcon = styled('div')`
  display: flex;
  align-items: center;
  flex: 1;
  border: 2px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  min-height: 40px;
  padding: 0 10px;
  gap: 8px;
`;

const SearchInput = styled('input')`
  border: none;
  outline: none;
  width: 100%;
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;

  ::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const DoneButton = styled('button')`
  border: none;
  background: none;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  padding: 8px;
  cursor: pointer;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.hover};
    border-radius: 4px;
  }
`;

const ListBody = styled('div')`
  flex: 1;
  /* min-height:0 lets this flex child actually shrink so the footer below it
     is never pushed off-screen (default min-height:auto would clip it) */
  min-height: 0;
  /* Do NOT scroll here: the react-window List inside is its own scroll
     container. A momentum-scroll layer on this parent (overflow + webkit
     touch scrolling) nests two scrollers and swallows the first tap on a row
     (the "two taps to select" bug). The List handles all scrolling. */
  overflow: hidden;
`;

const Row = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;

  &:active {
    background: ${({ theme }) => theme.colors.surfaceHoverStrong};
  }
`;

const RowLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const IconContainer = styled('div')`
  height: 28px;
  width: 28px;
  min-width: 28px;
  border-radius: ${(props) => (props.square ? '4px' : '50%')};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  ${(props) => props.backgroundColor && `background: ${props.backgroundColor};`}
  ${(props) => props.backgroundColor && `border: 1px solid ${props.backgroundColor};`}
`;

const RowName = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  padding: 24px 16px;
`;

const Footer = styled('div')`
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 12px;
  flex-shrink: 0;
`;

const CreateCta = styled('button')`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: none;
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: 4px 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.hover};
    border-radius: 4px;
  }
`;

const CreateLabel = styled('span')`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* matches the dropdown footer's create-icon box (bordered white square) */
const CreateIconBox = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  max-width: 24px;
  min-height: 24px;
  max-height: 24px;
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  flex-shrink: 0;
`;

const QuickIconRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const QuickIconBox = styled('button')`
  height: 32px;
  width: 32px;
  border: none;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: ${(props) => props.backgroundColor || 'transparent'};
`;

/** stable empty default so a momentarily-undefined slice doesn't hand
 * useSelector a fresh [] each render (which would churn re-renders) */
const EMPTY_ARRAY = [];

const MobileAddExercisePicker = ({
  isOpen, onClose, date, dayNumber, mode, typeToFilter, onSwapSelect,
}) => {
  const { colors } = useTheme();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const isSwap = mode === 'swap';
  /** pointer position at press-start, to tell a tap from a scroll drag */
  const pointerStartRef = useRef(null);

  const allExercises = useSelector((state) => state.calendar.data.allExercises || EMPTY_ARRAY);
  const tagsForAccount = useSelector((state) => state.calendar.data.tagsForAccount || EMPTY_ARRAY);
  const accessoryItems = useSelector(
    (state) => state.calendar.data.calendarItemContainers?.accessoryCalendarItems || EMPTY_ARRAY,
  );
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const hasExerciseManagementAccess = currentUser?.userSettings?.exercises === 1;

  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(effectiveDate).format('YYYY-MM-DD');

  /** Add mode: exercises + tags + accessory quick-adds (rest, video, session
   * break). Swap mode: only exercises, filtered to the swapped item's type -
   * mirrors the dropdown's exerciseOptions/typeToFilter behavior. */
  const options = useMemo(() => {
    /** shared cache - one copy per allExercises array across every picker */
    const exerciseOptions = getExerciseOptions(allExercises, isSwap ? typeToFilter : null);
    if (isSwap) return exerciseOptions;

    const tagOptions = tagsForAccount.map((tag) => ({
      ...tag,
      name: tag.name,
      exerciseId: tag.id,
      icon: 'tag2',
      iconColor: '#FF4500',
      squareIconContainer: true,
      iconBackgroundColor: 'rgba(255, 69, 0, 0.2)',
    }));
    const accessoryOptions = accessoryItems.map((exercise) => ({
      ...exercise,
      name: exercise.name,
      exerciseId: exercise.id,
      squareIconContainer: true,
      iconBackgroundColor: colors.surface,
    }));
    return [...exerciseOptions, ...tagOptions, ...accessoryOptions];
  }, [allExercises, tagsForAccount, accessoryItems, isSwap, typeToFilter]);

  /** Same search as the desktop dropdown (shared helper) so results match. */
  const results = useMemo(
    () => searchExerciseOptions(options, searchTerm),
    [options, searchTerm],
  );

  /** Identical to SelectDropdownExercisePicker.handleAddExercise. Keeps the
   * picker open so several items can be added in one session. */
  const handleAddExercise = (option) => {
    const newItem = {
      ...option,
      type: option.type || 'T',
      icon: option.icon,
      title: option.name,
      exerciseId: parseInt(option.id, 10),
      id: `${option.id}-${generateUniqueId()}`,
      assignedId: `${option.id}-${generateUniqueId()}`,
      preSave: true,
    };
    if (programType === 'free-form-program') {
      dispatch(addPreSavedItemToProgramItemsArray(newItem, dayNumber || selectedProgramDayNumber));
    } else {
      dispatch(addPreSavedItemToCalendarItemsArray(newItem, endpointFormattedDate));
    }
  };

  /** We select on pointerup, but iOS still fires a synthesized click ~300ms
   * later. In SWAP mode the picker closes on select, so that trailing click
   * would land on the calendar behind (click-through). Swallow the next click
   * at the document level. (ADD mode never needs this: it stays open, so the
   * click lands harmlessly on the overlay, whose rows have no onClick.) */
  const swallowNextClick = () => {
    let timer;
    const swallow = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
      window.removeEventListener('click', swallow, true);
      if (timer) window.clearTimeout(timer);
    };
    window.addEventListener('click', swallow, true);
    timer = window.setTimeout(() => {
      window.removeEventListener('click', swallow, true);
    }, 500);
  };

  /** Swap: hand the picked exercise to the item's swap handler (which drives
   * the same table swap the desktop dropdown does). Add: append a pre-save
   * item. */
  const handleRowSelect = (option) => {
    if (isSwap) {
      swallowNextClick();
      if (onSwapSelect) onSwapSelect(option);
      onClose();
    } else {
      handleAddExercise(option);
    }
  };

  const openCreateModal = (type) => {
    dispatch(setActiveModal('createNewComponentModal', {
      type,
      openedFromExercisePicker: true,
      targetDate: endpointFormattedDate,
    }));
    onClose();
  };

  if (!isOpen) return null;

  const hasResults = results.length > 0;

  /* Portaled to document.body so the overlay escapes the exercise item's
   * subtree. When it lived inside DraggableContent's Container, every tap on a
   * row first fired Container's onClickCapture (setActiveExerciseId dispatch),
   * which swallowed the first tap - the "two taps to select" bug. The add
   * picker never had this because it renders outside any such Container. */
  return createPortal(
    <Overlay>
      <Header>
        <SearchAndIcon>
          <IcomoonReact iconSet={iconSet} size={16} icon='magnifying-glass-alt' color={colors.textSecondary} />
          {/* intentionally not auto-focused: popping the keyboard on open
              covers the bottom footer (the Create button) */}
          <SearchInput
            type='text'
            placeholder='Search Exercises'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchAndIcon>
        <DoneButton type='button' onClick={onClose}>Done</DoneButton>
      </Header>

      <ListBody>
        {hasResults ? (
          /* virtualized so opening the picker doesn't mount thousands of rows
             (each with an inline SVG icon) at once - that was the ~1-2s lag */
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                width={width}
                itemCount={results.length}
                itemSize={52}
                style={{ overscrollBehavior: 'contain' }}
              >
                {({ index, style }) => {
                  const option = results[index];
                  return (
                    <Row
                      style={style}
                      key={option.assignedId || `${option.exerciseId}-${option.type}`}
                      /* Select on pointerup, not onClick: inside a scrollable
                       * virtualized list iOS drops the first synthesized click
                       * (the "two taps to select" bug). pointerup fires on the
                       * real finger-lift regardless. The move threshold keeps a
                       * scroll gesture from selecting; preventDefault suppresses
                       * the trailing compatibility click so closing the overlay
                       * doesn't click through to the calendar behind it. */
                      onPointerDown={(e) => {
                        pointerStartRef.current = { x: e.clientX, y: e.clientY };
                      }}
                      onPointerUp={(e) => {
                        const start = pointerStartRef.current;
                        pointerStartRef.current = null;
                        if (start
                          && Math.abs(e.clientX - start.x) <= 10
                          && Math.abs(e.clientY - start.y) <= 10) {
                          e.preventDefault();
                          handleRowSelect(option);
                        }
                      }}
                      /* clear the start point if the gesture is cancelled
                         (e.g. the browser takes over for a scroll) so stale
                         coordinates can't be reused by the next pointerup */
                      onPointerCancel={() => {
                        pointerStartRef.current = null;
                      }}
                    >
                      <RowLeft>
                        <IconContainer
                          square={option.squareIconContainer}
                          backgroundColor={option.iconBackgroundColor}
                        >
                          <IcomoonReact
                            iconSet={iconSet}
                            size={16}
                            icon={option.icon}
                            color={option.iconColor || colors.text}
                          />
                        </IconContainer>
                        <RowName>{option.name}</RowName>
                      </RowLeft>
                      {option.media?.length ? (
                        <IcomoonReact iconSet={iconSet} size={16} icon='video-camera' color={colors.textSecondary} />
                      ) : null}
                    </Row>
                  );
                }}
              </List>
            )}
          </AutoSizer>
        ) : (
          <EmptyState>{`No exercises match "${searchTerm}"`}</EmptyState>
        )}
      </ListBody>

      {/* Swap mode is a pure picker - no create/quick-add footer */}
      {!isSwap && (
        <Footer>
          <CreateCta
            type='button'
            onClick={() => openCreateModal(
              !hasExerciseManagementAccess ? 'N' : undefined,
            )}
          >
            <CreateIconBox>
              <IcomoonReact iconSet={iconSet} size={10} icon='plus' color={colors.textSecondary} />
            </CreateIconBox>
            {!hasResults && !!searchTerm ? (
              <CreateLabel>{`Add "${searchTerm}..."`}</CreateLabel>
            ) : (
              <CreateLabel>Create New Exercise</CreateLabel>
            )}
          </CreateCta>
          <QuickIconRow>
            <QuickIconBox
              type='button'
              backgroundColor='rgba(249, 191, 59, 0.2)'
              onClick={() => openCreateModal('N')}
            >
              <IcomoonReact iconSet={iconSet} size={15} icon='note' color='#F9BF3B' />
            </QuickIconBox>
            <QuickIconBox
              type='button'
              backgroundColor='rgba(231, 76, 60, 0.2)'
              onClick={() => openCreateModal('W')}
            >
              <IcomoonReact iconSet={iconSet} size={15} icon='warmup' color='#E74C3C' />
            </QuickIconBox>
          </QuickIconRow>
        </Footer>
      )}
    </Overlay>,
    document.body,
  );
};

MobileAddExercisePicker.defaultProps = {
  date: null,
  dayNumber: null,
  mode: 'add',
  typeToFilter: null,
  onSwapSelect: null,
};

MobileAddExercisePicker.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.number,
  mode: PropTypes.oneOf(['add', 'swap']),
  typeToFilter: PropTypes.string,
  onSwapSelect: PropTypes.func,
};

export default MobileAddExercisePicker;
